import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Menu, Shield, User, HelpCircle } from 'lucide-react';
import { useState } from 'react';
import { useWalkthrough } from './OnboardingWalkthrough';

const Navbar = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { startWalkthrough } = useWalkthrough();

  // Check if user is admin
  const isAdmin = user?.is_admin || user?.email === 'contact@blendonics.com';
  
  // Get effective tier
  const getEffectiveTier = () => {
    if (isAdmin) return 'elite';
    if (user?.membership_status === 'lifetime' && user?.has_course_access) return 'starter';
    if (user?.membership_status === 'active' || user?.membership_status === 'cancelling') {
      return user?.membership_tier || 'free';
    }
    return 'free';
  };
  
  const tier = getEffectiveTier();
  
  // Access checks based on tier
  const canAccessContent = tier !== 'free';
  const canAccessCommunity = tier !== 'free';
  const canAccessCoaching = tier === 'elite' || isAdmin;

  return (
    <nav className="glass-panel sticky top-0 z-50 border-b">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Mobile: Menu button on left */}
          <div className="md:hidden flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              <Menu size={24} />
            </Button>
          </div>

          {/* Desktop: Logo on left */}
          <Link to="/" className="hidden md:flex items-center" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_beatblend-63/artifacts/255zmwv6_White%20logo%20-%20no%20background.svg" 
              alt="Blendonics" 
              className="h-10 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-6">
            {user ? (
              <>
                <Link to="/dashboard" className="transition-colors" style={{ color: '#ffffff' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#ffffff'} data-testid="nav-dashboard" data-tour="dashboard">
                  Dashboard
                </Link>
                {canAccessContent && (
                  <Link to="/content" className="transition-colors" style={{ color: '#ffffff' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#ffffff'} data-testid="nav-content" data-tour="content">
                    Content
                  </Link>
                )}
                {canAccessCommunity && (
                  <Link to="/community" className="transition-colors" style={{ color: '#ffffff' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#ffffff'} data-testid="nav-community" data-tour="community">
                    DJ Community
                  </Link>
                )}
                {canAccessCoaching && (
                  <Link to="/coaching" className="transition-colors" style={{ color: '#ffffff' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#ffffff'} data-testid="nav-coaching">
                    Coaching
                  </Link>
                )}
                <Link to="/pricing" className="transition-colors" style={{ color: '#ffffff' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#ffffff'} data-testid="nav-pricing">
                  Pricing
                </Link>
                <Link to="/referral" className="transition-colors" style={{ color: '#ffffff' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#ffffff'} data-testid="nav-referral">
                  Referral
                </Link>
                {isAdmin && (
                  <Link 
                    to="/admin" 
                    className="flex items-center gap-1 transition-colors" 
                    style={{ color: '#D4AF37' }} 
                    onMouseEnter={(e) => e.target.style.opacity = '0.8'} 
                    onMouseLeave={(e) => e.target.style.opacity = '1'} 
                    data-testid="nav-admin"
                  >
                    <Shield size={16} />
                    Admin
                  </Link>
                )}
                <button
                  onClick={startWalkthrough}
                  className="p-2 rounded-full transition-all hover:bg-white/10"
                  title="Platform Tour"
                  data-testid="nav-tour-btn"
                >
                  <HelpCircle size={18} style={{ color: '#D4AF37' }} />
                </button>
                <Link 
                  to="/account" 
                  className="flex items-center gap-2 text-sm transition-colors hover:opacity-80" 
                  style={{ color: '#D4AF37' }}
                  data-testid="nav-account"
                >
                  <User size={16} />
                  {user.name}
                </Link>
                <Button onClick={logout} variant="outline" size="sm" data-testid="nav-logout">
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/about" className="transition-colors" style={{ color: '#ffffff' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#ffffff'} data-testid="nav-about">
                  About Us
                </Link>
                <Link to="/pricing" className="transition-colors" style={{ color: '#ffffff' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#ffffff'} data-testid="nav-pricing">
                  Memberships and Pricing
                </Link>
                <Link to="/reviews" className="transition-colors" style={{ color: '#ffffff' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#ffffff'} data-testid="nav-reviews">
                  Reviews
                </Link>
                <Link to="/referral" className="transition-colors" style={{ color: '#ffffff' }} onMouseEnter={(e) => e.target.style.color = '#D4AF37'} onMouseLeave={(e) => e.target.style.color = '#ffffff'} data-testid="nav-referral-guest">
                  Referral Program
                </Link>
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost">Login</Button>
                </Link>
                <Link to="/signup" data-testid="nav-signup">
                  <Button className="rounded-full transition-all duration-300" style={{ background: '#D4AF37', color: '#141414' }}>
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile: Logo on right */}
          <Link to="/" className="md:hidden flex items-center" data-testid="nav-logo-mobile">
            <img 
              src="https://customer-assets.emergentagent.com/job_beatblend-63/artifacts/255zmwv6_White%20logo%20-%20no%20background.svg" 
              alt="Blendonics" 
              className="h-8 w-auto"
            />
          </Link>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden pb-4 space-y-2" data-testid="mobile-menu">
            {user ? (
              <>
                <Link to="/dashboard" className="block py-2" style={{ color: '#ffffff' }} onClick={() => setMobileMenuOpen(false)}>
                  Dashboard
                </Link>
                {canAccessContent && (
                  <Link to="/content" className="block py-2" style={{ color: '#ffffff' }} onClick={() => setMobileMenuOpen(false)}>
                    Content
                  </Link>
                )}
                {canAccessCommunity && (
                  <Link to="/community" className="block py-2" style={{ color: '#ffffff' }} onClick={() => setMobileMenuOpen(false)}>
                    DJ Community
                  </Link>
                )}
                {canAccessCoaching && (
                  <Link to="/coaching" className="block py-2" style={{ color: '#ffffff' }} onClick={() => setMobileMenuOpen(false)}>
                    Coaching
                  </Link>
                )}
                <Link to="/pricing" className="block py-2" style={{ color: '#ffffff' }} onClick={() => setMobileMenuOpen(false)}>
                  Pricing
                </Link>
                <Link to="/referral" className="block py-2" style={{ color: '#ffffff' }} onClick={() => setMobileMenuOpen(false)}>
                  Referral
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="flex items-center gap-2 py-2" style={{ color: '#D4AF37' }} onClick={() => setMobileMenuOpen(false)}>
                    <Shield size={16} />
                    Admin Dashboard
                  </Link>
                )}
                <Link to="/account" className="flex items-center gap-2 py-2" style={{ color: '#D4AF37' }} onClick={() => setMobileMenuOpen(false)}>
                  <User size={16} />
                  Account Details
                </Link>
                <button
                  onClick={() => {
                    startWalkthrough();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-2 py-2 w-full text-left"
                  style={{ color: '#D4AF37' }}
                  data-testid="mobile-tour-btn"
                >
                  <HelpCircle size={16} />
                  Platform Tour
                </button>
                <Button onClick={() => { logout(); setMobileMenuOpen(false); }} variant="outline" className="w-full mt-2">
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/about" className="block py-2" style={{ color: '#ffffff' }} onClick={() => setMobileMenuOpen(false)}>
                  About Us
                </Link>
                <Link to="/pricing" className="block py-2" style={{ color: '#ffffff' }} onClick={() => setMobileMenuOpen(false)}>
                  Memberships and Pricing
                </Link>
                <Link to="/reviews" className="block py-2" style={{ color: '#ffffff' }} onClick={() => setMobileMenuOpen(false)}>
                  Reviews
                </Link>
                <Link to="/referral" className="block py-2" style={{ color: '#ffffff' }} onClick={() => setMobileMenuOpen(false)}>
                  Referral Program
                </Link>
                <Link to="/login" className="block py-2" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full">Login</Button>
                </Link>
                <Link to="/signup" className="block py-2" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full rounded-full" style={{ background: '#D4AF37', color: '#141414' }}>Get Started</Button>
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
